import React, { useState, useEffect } from 'react';
import { useToast } from '@/hooks/use-toast';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Metric } from './MetricsTable';
import { updatePricingMetric } from '@/utils/metricsUtils';

interface EditMetricModalProps {
  metric: Metric | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (updatedMetric: Metric) => void;
}

// Available modality options
const MODALITY_OPTIONS = [
  { value: 'Usage', label: 'Usage' },
  { value: 'License', label: 'License' },
  { value: 'Credit', label: 'Credit' },
  { value: 'Flat', label: 'Flat' },
  { value: 'Threshold', label: 'Threshold' },
  { value: 'Unknown', label: 'Unknown' }
];

const EditMetricModal: React.FC<EditMetricModalProps> = ({
  metric,
  isOpen,
  onClose,
  onSuccess
}) => {
  const { toast } = useToast();
  const [displayName, setDisplayName] = useState<string>('');
  const [modality, setModality] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initialize form when metric data is available
  useEffect(() => {
    if (metric) {
      setDisplayName(metric.displayName || '');
      setModality(metric.modality || 'Unknown');
    }
  }, [metric]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!metric?.id) {
      toast({
        title: "Error",
        description: "Metric ID is missing",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await updatePricingMetric(
        metric.id,
        {
          display_name: displayName,
          modality: modality
        }
      );

      if (result.success && result.updatedMetric) {
        toast({
          title: "Success",
          description: "Metric updated successfully"
        });
        
        // Create updated metric object for the UI
        const updatedMetric: Metric = {
          ...metric,
          displayName: result.updatedMetric.display_name || displayName,
          modality: result.updatedMetric.modality || modality
        };
        
        onSuccess(updatedMetric);
        
        // Use the global refresh function if available to refresh the UI immediately
        const refreshCompanyData = (window as any).refreshCompanyData;
        if (typeof refreshCompanyData === 'function') {
          console.log("Calling parent refreshCompanyData function after metric update");
          refreshCompanyData();
        }
        
        onClose();
      } else {
        toast({
          title: "Error",
          description: result.message || "Failed to update metric",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error updating metric:", error);
      toast({
        title: "Error",
        description: "An unexpected error occurred",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            Edit Metric
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 pt-4">
          <div>
            <Label htmlFor="displayName">Display Name</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter metric name"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="modality">Modality</Label>
            <select
              id="modality"
              value={modality}
              onChange={(e) => setModality(e.target.value)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 mt-1"
            >
              {MODALITY_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <DialogFooter className="pt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={onClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditMetricModal;
